/**
 * Shared SERP display helpers used by LinkedKeywordsPane and DiscoveryPane.
 */
import { relativeAge } from './relativeAge';

const ENGINE_ICON_MAP: Record<string, { title: string; color: string; path: string }> = {
  GOOGLE: {
    title: 'Google',
    color: '#4285F4',
    path: 'M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z',
  },
  BING: {
    title: 'Bing',
    color: '#008373',
    path: 'M2.01 0L0 2.973l8.318 4.476v9.067l6.493 3.484 9.179-4.979V8.164l-5.59 2.991v3.211l-3.589 1.937V9.542l-9.8-5.27z',
  },
  DUCKDUCKGO: {
    title: 'DuckDuckGo',
    color: '#DE5833',
    path: 'M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm0 .984C18.083.984 23.016 5.916 23.016 12S18.084 23.016 12 23.016.984 18.084.984 12C.984 5.917 5.916.984 12 .984zm0 .938C6.434 1.922 1.922 6.434 1.922 12c0 4.437 2.867 8.205 6.85 9.55-.237-.82-.776-2.753-1.6-6.052-1.184-4.741-2.064-8.606 2.379-9.813.047-.011.064-.064.03-.093-.514-.467-1.382-.548-2.233-.38a.06.06 0 0 1-.07-.058c0-.011 0-.023.011-.035.205-.286.572-.507.822-.64a1.843 1.843 0 0 0-.607-.335c-.059-.022-.059-.12-.006-.144.006-.006.012-.012.024-.012 1.749-.233 3.586.292 4.49 1.448.011.011.023.017.035.023 2.968.635 3.509 4.837 3.328 5.998a9.607 9.607 0 0 0 2.346-.576c.746-.286 1.008-.222 1.101-.053.1.193-.018.513-.28.81-.496.567-1.393 1.01-2.974 1.137-.546.044-1.029.024-1.445.006-.789-.035-1.339-.059-1.633.39-.192.298-.041.998 1.487 1.22 1.09.157 2.078.047 2.798-.034.643-.07 1.073-.118 1.172.069.21.402-.996 1.207-3.066 1.224-.158 0-.315-.006-.467-.011-1.283-.065-2.227-.414-2.816-.735a.094.094 0 0 1-.035-.017c-.105-.059-.31.045-.188.267.07.134.444.478 1.004.776-.058.466.087 1.184.338 2l.088-.016c.041-.009.087-.019.134-.025.507-.082.775.012.926.175.717-.536 1.913-1.294 2.03-1.154.583.694.66 2.332.53 2.99-.004.012-.017.024-.04.035-.274.117-1.783-.296-1.783-.511-.059-1.075-.26-1.173-.493-1.225h-.156c.006.006.012.018.018.03l.052.12c.093.257.24 1.063.13 1.26-.112.199-.835.297-1.284.303-.443.006-.543-.158-.637-.408-.07-.204-.103-.675-.103-.95a.857.857 0 0 1 .012-.216c-.134.058-.333.193-.397.281-.017.262-.017.682.123 1.149.07.221-1.518 1.164-1.74.99-.227-.181-.634-1.952-.459-2.67-.187.017-.338.075-.42.191-.367.508.093 2.933.582 3.248.257.169 1.54-.553 2.176-1.095.105.145.305.158.553.158.326-.012.782-.06 1.103-.158.192.45.423.972.613 1.388 4.47-1.032 7.803-5.037 7.803-9.82 0-5.566-4.512-10.078-10.078-10.078zm1.791 5.646c-.42 0-.678.146-.795.332-.023.047.047.094.094.07.14-.075.357-.161.701-.156.328.006.516.09.67.159l.023.01c.041.017.088-.03.059-.065-.134-.18-.332-.35-.752-.35zm-5.078.198a1.24 1.24 0 0 0-.522.082c-.454.169-.67.526-.67.76 0 .051.112.057.141.011.081-.123.21-.31.617-.478.408-.17.73-.146.951-.094.047.012.083-.041.041-.07a.989.989 0 0 0-.558-.211zm5.434 1.423a.651.651 0 0 0-.655.647.652.652 0 0 0 1.307 0 .646.646 0 0 0-.652-.647zm.283.262h.008a.17.17 0 0 1 .17.17c0 .093-.077.17-.17.17a.17.17 0 0 1-.17-.17c0-.09.072-.165.162-.17zm-5.358.076a.752.752 0 0 0-.758.758c0 .42.338.758.758.758s.758-.337.758-.758a.756.756 0 0 0-.758-.758zm.328.303h.01c.112 0 .2.089.2.2 0 .11-.088.197-.2.197a.195.195 0 0 1-.197-.198c0-.107.082-.194.187-.199z',
  },
};

export function EngineIcon({ engine, size = 'md', className = '' }: { engine: string | null; size?: 'sm' | 'md'; className?: string }) {
  const lookupKey = engine?.toUpperCase() || '';
  const icon = ENGINE_ICON_MAP[lookupKey];
  const containerSize = size === 'sm' ? 'h-6 w-6' : 'h-7 w-7';
  const glyphSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  if (!icon) {
    const fallbackLetter = lookupKey ? lookupKey.charAt(0) : '?';
    // A single letter in a circle is unreadable without the name — "B" is Bing
    // or Brave, and the only way to find out was to guess. `aria-label` told a
    // screen reader and nobody else.
    const label = engine ? `${engine} SERP` : 'Unknown SERP engine';
    return (
      <span
        className={`inline-flex ${containerSize} items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] font-semibold text-gray-600 dark:text-gray-300 ${className}`}
        aria-label={label}
        title={label}
      >
        {fallbackLetter}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex ${containerSize} items-center justify-center rounded-full bg-white dark:bg-gray-800 shadow-sm ring-1 ring-gray-200 ${className}`}
      aria-label={`${icon.title} SERP`}
      title={`${icon.title} SERP`}
    >
      <svg
        viewBox="0 0 24 24"
        className={`${glyphSize}`}
        role="img"
        aria-hidden="true"
        fill={icon.color}
      >
        <path d={icon.path} />
      </svg>
    </span>
  );
}

export function RefreshGlyph({ spinning = false }: { spinning?: boolean }) {
  return (
    <svg
      className={`h-3.5 w-3.5 ${spinning ? 'animate-spin' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
    </svg>
  );
}

export function getSearchUrl(engine: string | null, keyword: string): string {
  switch (engine?.toUpperCase()) {
    case 'GOOGLE': return `https://www.google.com/search?q=${encodeURIComponent(keyword)}`;
    case 'BING': return `https://www.bing.com/search?q=${encodeURIComponent(keyword)}`;
    case 'BRAVE': return `https://search.brave.com/search?q=${encodeURIComponent(keyword)}`;
    default: return `https://duckduckgo.com/?q=${encodeURIComponent(keyword)}`;
  }
}

export function ExternalSearchLink({ engine, keyword, onClick }: { engine: string | null; keyword: string; onClick?: () => void }) {
  return (
    <a
      href={getSearchUrl(engine, keyword)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 flex-shrink-0 p-1 -m-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
      title={`Open "${keyword}" search`}
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </svg>
    </a>
  );
}

export function formatSerpDate(dateStr: string | null): string | null {
  // Delegates now. This used to have its own scale — "Just now", "6h ago",
  // "2w ago", "Mar 5" — while the Trends freshness chip said "today" / "3d" /
  // "2mo", so one table could show two vocabularies for one idea. See
  // `common/relativeAge`.
  if (!dateStr) return null;
  return relativeAge(dateStr);
}
